import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { Button } from "@shared/ui/button";
import { ValueLabel } from "@features/ax/shared/ValueLabel";
import { AlertTriangle, Check, Info } from "lucide-react";
import { useCurrency } from "@shared/selectors/globalSelectors";

interface SubmitLotModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: () => void;
  data?: any;
}

export const SubmitLotModal: React.FC<SubmitLotModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  data,
}) => {
  const currency = useCurrency();

  if (!data) return null;

  const countStats = [
    {
      id: "physical",
      label: "Physical Count",
      value: data.physicalSlipCount || 0,
    },
    {
      id: "entered",
      label: "Total Entered",
      value: data.enteredCount || 0,
    },
    {
      id: "valid",
      label: "Valid-Will Post",
      value: data.validCount || 0,
    },
    {
      id: "flagged",
      label: "Flagged-Quarantine",
      value: data.flaggedCount || 0,
    },
  ];

  const amountStats = [
    {
      id: "lot-total",
      label: "Lot Total",
      value: `${currency} ${data.lotTotal}`,
    },
    {
      id: "est-post",
      label: "Estimated Post Amount",
      // Assuming valid slips make up the post amount, but here we can just use lot_total as a placeholder if API doesn't provide valid_total
      // Or maybe posted_total? The user's JSON has "posted_total" which might be after posting, so we can calculate it or just show lot_total.
      // Let's assume it's lot_total for now unless there's a specific field.
      value: `${currency} ${data.postedTotal}`,
    },
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[700px] flex flex-col p-0">
        <DialogHeader className="px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <DialogTitle className="flex items-center gap-2 text-lg font-semibold text-gray-900">
            <Check className="w-5 h-5 text-gray-700" />
            Submit Lot for Auto-Post
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
          {/* Warning Banner */}
          {data.count_mismatch > 0 && (
            <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-lg flex items-start gap-3 text-sm">
              <AlertTriangle className="w-5 h-5 shrink-0 text-amber-500" />
              <p>
                <span className="font-bold">Physical count mismatch:</span>{" "}
                physical count is {data.physical_slip_count} but only{" "}
                {data.entered_count} slips were entered. {data.count_mismatch}{" "}
                slips missing.
              </p>
            </div>
          )}

          {/* Consolidated Stats Card */}
          <div className="border shadow-sm rounded-xl overflow-hidden bg-white">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 p-6 border-b border-gray-100">
              {countStats.map((stat) => (
                <ValueLabel
                  key={stat.id}
                  label={stat.label}
                  value={stat.value}
                />
              ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6 bg-gray-50/50">
              {amountStats.map((stat) => (
                <ValueLabel
                  key={stat.id}
                  label={stat.label}
                  value={stat.value}
                />
              ))}
            </div>
          </div>

          {/* Info Banner */}
          <div className="bg-blue-50/50 border border-blue-200 text-blue-800 px-4 py-4 rounded-lg flex items-start gap-3 text-sm">
            <Info className="w-5 h-5 shrink-0 text-blue-500 mt-0.5" />
            <p className="leading-relaxed">
              On submit, valid slips are{" "}
              <span className="font-bold">auto-posted to consumer ledgers</span>{" "}
              immediately. Flagged slips move to the exception queue for
              resolution. A final cross-lot duplicate scan runs before posting.
            </p>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              onSubmit();
              onClose();
            }}
            variant="default"
          >
            <Check className="w-4 h-4 mr-2" />
            Submit & Auto-Post
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
